import { Button } from "@/components/ui/button";
import { MonthPicker } from "@/components/ui/monthPicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useFormatters } from "@/lib/hooks/useFormatters";

import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonGroup } from "./ui/button-group";
import { useAdjacentMonthDisabled } from "@/hooks/useAdjacentMonthDisabled";
import { useYearRange } from "@/hooks/useYearRange";

interface MonthYearNavigationProps {
  className?: string;
  popoverClassName?: string;
  value?: Date;
  onMonthChange?: (date: Date) => void;
  disabled?: boolean;
  emptyStateLabel?: string;
  startYearOffset?: number;
  endYearOffset?: number;
}

export default forwardRef<HTMLButtonElement, MonthYearNavigationProps>(
  function MonthYearNavigation(
    {
      className,
      popoverClassName,
      value,
      onMonthChange,
      disabled,
      emptyStateLabel,
      startYearOffset = 5,
      endYearOffset = 5,
      ...props
    },
    ref,
  ) {
    const { t } = useTranslation();
    const { formatMonthYear } = useFormatters();
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);
    const { minYear, maxYear } = useYearRange(startYearOffset, endYearOffset);

    // For calendar display
    const [calendarDate, setCalendarDate] = useState<Date | undefined>(() =>
      value ? new Date(value) : undefined,
    );

    useEffect(() => {
      if (value) {
        setSelectedDate(new Date(value));
        setCalendarDate(new Date(value));
      } else {
        setSelectedDate(undefined);
        setCalendarDate(undefined);
      }
    }, [value]);

    const setSelectedDateInternal = useCallback(
      (date: Date) => {
        const newDate = new Date(date);
        setSelectedDate(newDate);
        onMonthChange?.(newDate);
      },
      [onMonthChange],
    );

    const handleMonthChange = useCallback(
      (date: Date) => {
        setSelectedDateInternal(date);
        setCalendarDate(new Date(date));
      },
      [setSelectedDateInternal],
    );

    const onNextMonthClick = useCallback(() => {
      if (selectedDate) {
        selectedDate.setMonth(selectedDate.getMonth() + 1);
        setSelectedDateInternal(selectedDate);
        setCalendarDate(selectedDate);
      } else if (emptyStateLabel && onMonthChange) {
        // When empty state, use current month + 1
        const nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        setSelectedDateInternal(nextMonth);
        setCalendarDate(nextMonth);
      }
    }, [selectedDate, setSelectedDateInternal, emptyStateLabel, onMonthChange]);

    const onPreviousMonthClick = useCallback(() => {
      if (selectedDate) {
        selectedDate.setMonth(selectedDate.getMonth() - 1);
        setSelectedDateInternal(selectedDate);
        setCalendarDate(selectedDate);
      } else if (emptyStateLabel && onMonthChange) {
        // When empty state, use current month - 1
        const prevMonth = new Date();
        prevMonth.setMonth(prevMonth.getMonth() - 1);
        setSelectedDateInternal(prevMonth);
        setCalendarDate(prevMonth);
      }
    }, [selectedDate, setSelectedDateInternal, emptyStateLabel, onMonthChange]);

    const showEmptyState = !selectedDate && emptyStateLabel;
    const navigationDisabled =
      Boolean(disabled) || (!selectedDate && !emptyStateLabel);
    const { disablePreviousMonth, disableNextMonth } = useAdjacentMonthDisabled(
      {
        minYear,
        maxYear,
        selectedDate,
        disabled: navigationDisabled,
      },
    );

    return (
      <div
        className={cn("flex flex-row gap-2", className)}
        data-testid="month-year-navigation"
      >
        <ButtonGroup>
          <Button
            variant="outline"
            className="min-w-[7rem] shrink-0"
            onClick={onPreviousMonthClick}
            disabled={disablePreviousMonth}
          >
            <ChevronLeftIcon className="mr-1 h-4 w-4" />
            {t("monthYearFilter.previousMonth")}
          </Button>
          {showEmptyState ? (
            <Button
              variant="outline"
              disabled
              className="min-w-[11rem] shrink-0 text-muted-foreground"
            >
              <CalendarIcon className="mr-1 h-4 w-4" />
              {emptyStateLabel}
            </Button>
          ) : (
            <Popover>
              <PopoverTrigger asChild className={popoverClassName}>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-medium min-w-[11rem] shrink-0",
                    !selectedDate && "text-muted-foreground",
                  )}
                  {...props}
                  ref={ref}
                  disabled={disabled}
                >
                  <CalendarIcon className="mr-1 h-4 w-4" />
                  {selectedDate ? (
                    formatMonthYear(selectedDate)
                  ) : (
                    <span>{t("monthYearFilter.pickMonth")}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <MonthPicker
                  value={calendarDate}
                  onChange={handleMonthChange}
                  disabled={disabled}
                  startYearOffset={startYearOffset}
                  endYearOffset={endYearOffset}
                />
              </PopoverContent>
            </Popover>
          )}
          <Button
            variant="outline"
            className="min-w-[7rem] shrink-0"
            onClick={onNextMonthClick}
            disabled={disableNextMonth}
          >
            {t("monthYearFilter.nextMonth")}
            <ChevronRightIcon className="ml-1 h-4 w-4" />
          </Button>
        </ButtonGroup>
      </div>
    );
  },
);
